import { useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm, type Resolver, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Car, CloudRain, Loader2, X } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import { Switch } from '@/components/atoms/switch';
import { Label } from '@/components/atoms/label';
import { TimePicker } from '@/components/molecules/time-picker';
import { BlackoutDatesEditor } from '@/components/molecules/BlackoutDatesEditor';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { FormPageLayout } from '@/components/layouts';
import {
  VisitBufferDiagram,
  WeeklyHoursVisualization,
} from '@/components/organisms/settings-scheduling/SchedulingDesign';
import { useBusinessSettings } from '@/hooks/settings/useBusinessSettings';
import { useUpdateBusinessSettings } from '@/hooks/settings/useUpdateBusinessSettings';
import { useHasPermission } from '@/hooks/usePermissions';
import type { BusinessBlackoutDateInput, BusinessHours, BusinessSettings } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/settings/office-settings/edit')({
  component: EditSchedulingSettingsPage,
});

const FORM_ID = 'scheduling-settings-form';

const DAYS: BusinessHours['day'][] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DAY_LABELS: Record<BusinessHours['day'], string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const SLOT_OPTIONS = [15, 30, 60, 120] as const;

const businessHoursSchema = z.object({
  day: z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']),
  open: z.boolean(),
  openTime: z.string(),
  closeTime: z.string(),
});

const schema = z.object({
  businessHours: z.array(businessHoursSchema),
  slotDurationMinutes: z.coerce.number().int().min(1),
  leadTimeHours: z.coerce.number().int().min(0),
  maxAdvanceBookingDays: z.coerce.number().int().min(1),
  emergencyEnabled: z.boolean(),
  geofenceAutoArrival: z.boolean(),
  geofenceAutoStart: z.boolean(),
  allowOverlappingSchedules: z.boolean(),
  bufferPrepMinutes: z.coerce.number().int().min(0),
  bufferCleanupMinutes: z.coerce.number().int().min(0),
  travelAvgSpeedKmh: z.coerce.number().int().min(1).max(200),
  maxAutoTimeShiftMinutes: z.coerce.number().int().min(0).max(240),
  blackoutDates: z.array(z.unknown()),
  // NOT-322 — per-tenant calendar prefs.
  weekStartDay: z.coerce.number().int().min(0).max(6),
  workWeekStartDay: z.coerce.number().int().min(0).max(6),
  defaultCalendarView: z.enum(['week', 'workweek', 'month', 'list', 'board']),
  // Scheduling v2 — routing & arrival-window knobs.
  eveningWindowSendTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  committedWindowToleranceMin: z.coerce.number().int().min(5).max(240),
  // Edited as a percent (50–100); converted to the stored 0.50–1.00 fraction
  // at the submit boundary.
  efficiencyAlertThresholdPct: z.coerce.number().int().min(50).max(100),
  weatherFlexDays: z.array(
    z.object({
      weekday: z.coerce.number().int().min(0).max(6),
      maxVisitsPerTech: z.coerce.number().int().min(1).nullable(),
    }),
  ),
  weatherFlexSpillover: z.enum(['NEXT_FLEX_DAY', 'EXTEND_WINDOW']),
});

type FormValues = z.infer<typeof schema>;

// NOT-322 — 0–6 (0=Sunday) matches FullCalendar firstDay / date-fns weekStartsOn.
const WEEKDAY_OPTIONS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
const CALENDAR_VIEW_OPTIONS = [
  { value: 'week', label: 'Week' },
  { value: 'workweek', label: 'Work Week' },
  { value: 'month', label: 'Month' },
  { value: 'list', label: 'List' },
  { value: 'board', label: 'Board' },
] as const;

/** Keep the editor ordered without inventing working hours for missing days. */
function seedBusinessHours(hours: BusinessHours[]): BusinessHours[] {
  return DAYS.map((day) => {
    const saved = hours.find((h) => h.day === day);
    if (saved) return saved;
    return { day, open: false, openTime: '00:00', closeTime: '00:00' };
  });
}

function EditSchedulingSettingsPage() {
  const navigate = Route.useNavigate();
  const { data: settings, isLoading } = useBusinessSettings();

  function goToView() {
    void navigate({ to: '/dashboard/settings/office-settings' });
  }

  if (isLoading || !settings) {
    return (
      <FormPageLayout title="Scheduling" onBack={goToView}>
        <div className="mx-auto w-full max-w-[1600px] space-y-4 px-3 py-5 sm:px-6">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-36 w-full rounded-xl" />
        </div>
      </FormPageLayout>
    );
  }

  return <SchedulingSettingsForm settings={settings} onDone={goToView} />;
}

function SchedulingSettingsForm({
  settings,
  onDone,
}: {
  settings: BusinessSettings;
  onDone: () => void;
}) {
  const canEdit = useHasPermission('business_hours:modify');
  const update = useUpdateBusinessSettings();

  const form = useForm<FormValues, unknown, FormValues>({
    resolver: zodResolver(schema) as Resolver<FormValues, unknown, FormValues>,
    defaultValues: {
      businessHours: seedBusinessHours(settings.businessHours),
      slotDurationMinutes: settings.slotDurationMinutes,
      leadTimeHours: settings.leadTimeHours,
      maxAdvanceBookingDays: settings.maxAdvanceBookingDays,
      emergencyEnabled: settings.emergencyEnabled,
      geofenceAutoArrival: settings.geofenceAutoArrival,
      geofenceAutoStart: settings.geofenceAutoStart,
      allowOverlappingSchedules: settings.allowOverlappingSchedules ?? false,
      bufferPrepMinutes: settings.bufferPrepMinutes,
      bufferCleanupMinutes: settings.bufferCleanupMinutes,
      travelAvgSpeedKmh: settings.travelAvgSpeedKmh,
      maxAutoTimeShiftMinutes: settings.maxAutoTimeShiftMinutes,
      blackoutDates: settings.blackoutDates,
      weekStartDay: settings.weekStartDay,
      workWeekStartDay: settings.workWeekStartDay,
      defaultCalendarView: settings.defaultCalendarView,
      eveningWindowSendTime: settings.eveningWindowSendTime,
      committedWindowToleranceMin: settings.committedWindowToleranceMin,
      efficiencyAlertThresholdPct: Math.round(settings.efficiencyAlertThreshold * 100),
      weatherFlexDays: settings.weatherFlexDays,
      weatherFlexSpillover: settings.weatherFlexSpillover,
    },
  });

  const hours = form.watch('businessHours');
  const flexDays = form.watch('weatherFlexDays');
  const [selectedBusinessDay, setSelectedBusinessDay] = useState<BusinessHours['day']>('saturday');

  function setWeatherFlexDay(weekday: number, enabled: boolean) {
    const current = form.getValues('weatherFlexDays');
    const next = enabled
      ? current.some((d) => d.weekday === weekday)
        ? current
        : [...current, { weekday, maxVisitsPerTech: null }]
      : current.filter((d) => d.weekday !== weekday);
    form.setValue(
      'weatherFlexDays',
      next.sort((a, b) => a.weekday - b.weekday),
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  }

  function setWeatherFlexLimit(weekday: number, raw: string) {
    const value = raw.trim() === '' ? null : Number(raw);
    form.setValue(
      'weatherFlexDays',
      form
        .getValues('weatherFlexDays')
        .map((day) => (day.weekday === weekday ? { ...day, maxVisitsPerTech: value } : day)),
      { shouldDirty: true, shouldValidate: true },
    );
  }

  function onSubmit(values: FormValues) {
    update.mutate(
      {
        businessHours: values.businessHours,
        slotDurationMinutes: values.slotDurationMinutes,
        leadTimeHours: values.leadTimeHours,
        maxAdvanceBookingDays: values.maxAdvanceBookingDays,
        emergencyEnabled: values.emergencyEnabled,
        geofenceAutoArrival: values.geofenceAutoArrival,
        geofenceAutoStart: values.geofenceAutoStart,
        allowOverlappingSchedules: values.allowOverlappingSchedules,
        bufferPrepMinutes: values.bufferPrepMinutes,
        bufferCleanupMinutes: values.bufferCleanupMinutes,
        travelAvgSpeedKmh: values.travelAvgSpeedKmh,
        maxAutoTimeShiftMinutes: values.maxAutoTimeShiftMinutes,
        blackoutDates: values.blackoutDates as BusinessBlackoutDateInput[],
        weekStartDay: values.weekStartDay,
        workWeekStartDay: values.workWeekStartDay,
        defaultCalendarView: values.defaultCalendarView,
        eveningWindowSendTime: values.eveningWindowSendTime,
        committedWindowToleranceMin: values.committedWindowToleranceMin,
        // Edited as a percent; stored as a 0.50–1.00 fraction.
        efficiencyAlertThreshold: values.efficiencyAlertThresholdPct / 100,
        weatherFlexDays: values.weatherFlexDays,
        weatherFlexSpillover: values.weatherFlexSpillover,
      },
      // Save returns to the read-only view with the updated values.
      { onSuccess: onDone },
    );
  }

  return (
    <FormPageLayout
      title="Scheduling"
      titleExtra={
        <span className="inline-flex items-center gap-2 text-sm font-normal text-muted-foreground">
          Editing · Times use {settings.timezone}
          {form.formState.isDirty ? (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-foreground">
              Unsaved changes
            </span>
          ) : null}
        </span>
      }
      onBack={onDone}
      actions={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onDone}>
            Discard
          </Button>
          <Button
            type="submit"
            size="sm"
            form={FORM_ID}
            disabled={!canEdit || update.isPending || !form.formState.isDirty}
          >
            {update.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </>
      }
    >
      <div className="min-h-full bg-[#fafafa]">
        <Form {...form}>
          {/* noValidate: native bubbles suppress the zod FormMessage errors —
              transient browser tooltips are easy to miss and set no aria-invalid. */}
          <form
            id={FORM_ID}
            noValidate
            onSubmit={form.handleSubmit(onSubmit)}
            className="mx-auto w-full max-w-[1600px] space-y-5 px-3 py-5 pb-24 sm:px-6"
          >
            <SchedulingEditDesign
              form={form}
              hours={hours}
              flexDays={flexDays}
              selectedBusinessDay={selectedBusinessDay}
              setSelectedBusinessDay={setSelectedBusinessDay}
              setWeatherFlexDay={setWeatherFlexDay}
              setWeatherFlexLimit={setWeatherFlexLimit}
            />
          </form>
        </Form>
      </div>
    </FormPageLayout>
  );
}

function SchedulingEditDesign({
  form,
  hours,
  flexDays,
  selectedBusinessDay,
  setSelectedBusinessDay,
  setWeatherFlexDay,
  setWeatherFlexLimit,
}: {
  form: UseFormReturn<FormValues>;
  hours: BusinessHours[];
  flexDays: FormValues['weatherFlexDays'];
  selectedBusinessDay: BusinessHours['day'];
  setSelectedBusinessDay: (day: BusinessHours['day']) => void;
  setWeatherFlexDay: (weekday: number, enabled: boolean) => void;
  setWeatherFlexLimit: (weekday: number, raw: string) => void;
}) {
  const selectedBusinessDayIndex = hours.findIndex((hour) => hour.day === selectedBusinessDay);
  const selectedHours = hours[selectedBusinessDayIndex];
  const [selectedFlexWeekday, setSelectedFlexWeekday] = useState(() => flexDays[0]?.weekday ?? 1);
  const selectedFlexConfig =
    flexDays.find((day) => day.weekday === selectedFlexWeekday) ?? flexDays[0];
  const activeFlexWeekday = selectedFlexConfig?.weekday ?? selectedFlexWeekday;
  const slotDurationMinutes = form.watch('slotDurationMinutes');
  const currentWeeklySlots = calculateWeeklySlots(hours, slotDurationMinutes);
  const defaultWeeklySlots = calculateWeeklySlots(
    (form.formState.defaultValues?.businessHours as BusinessHours[] | undefined) ?? hours,
    form.formState.defaultValues?.slotDurationMinutes ?? slotDurationMinutes,
  );

  return (
    <>
      <section className="overflow-hidden rounded-xl border border-[#dededb] bg-white text-[#171719]">
        <div className="grid lg:grid-cols-[minmax(0,754fr)_minmax(0,548fr)]">
          <div className="min-w-0">
            <header className="flex min-h-[43px] items-center gap-4 border-b border-[#dededb] px-[18px]">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#666]">
                When we work
              </h2>
              <span className="ml-auto text-[11.5px] text-[#666]">
                Select a day to change its hours
              </span>
            </header>
            <WeeklyHoursVisualization
              hours={hours}
              selectedDay={selectedBusinessDay}
              onSelectDay={setSelectedBusinessDay}
              weatherFlexWeekdays={flexDays.map((day) => day.weekday)}
              showLegend={false}
            />
            {selectedBusinessDayIndex >= 0 && selectedHours ? (
              <div
                key={selectedBusinessDay}
                className="flex min-h-[47px] flex-wrap items-center gap-3 border-t border-[#dededb] bg-[rgba(57,138,255,.05)] px-[18px] py-2"
              >
                <FormField
                  control={form.control}
                  name={`businessHours.${selectedBusinessDayIndex}.open`}
                  render={({ field }) => (
                    <div className="flex items-center gap-2.5">
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id={`business-day-${selectedBusinessDay}`}
                      />
                      <Label
                        htmlFor={`business-day-${selectedBusinessDay}`}
                        className="cursor-pointer text-[12.5px] font-semibold"
                      >
                        {DAY_LABELS[selectedBusinessDay]}
                      </Label>
                    </div>
                  )}
                />
                {selectedHours.open ? (
                  <div className="ml-auto flex items-center gap-2">
                    <FormField
                      control={form.control}
                      name={`businessHours.${selectedBusinessDayIndex}.openTime`}
                      render={({ field }) => (
                        <TimePicker value={field.value ?? '00:00'} onChange={field.onChange} />
                      )}
                    />
                    <span className="text-[11.5px] text-[#666]">to</span>
                    <FormField
                      control={form.control}
                      name={`businessHours.${selectedBusinessDayIndex}.closeTime`}
                      render={({ field }) => (
                        <TimePicker value={field.value ?? '00:00'} onChange={field.onChange} />
                      )}
                    />
                  </div>
                ) : (
                  <span className="ml-auto text-[11.5px] text-[#666]">Closed</span>
                )}
              </div>
            ) : null}
          </div>

          <div className="border-t border-[#dededb] lg:border-l lg:border-t-0">
            <header className="flex min-h-[43px] items-center border-b border-[#dededb] px-[18px]">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#666]">
                How customers can book
              </h2>
            </header>
            <FormField
              control={form.control}
              name="slotDurationMinutes"
              render={({ field }) => (
                <div className="border-b border-[#dededb] px-[18px] py-2.5">
                  <label className="mb-1 block text-[11.5px] font-medium">Slot length</label>
                  <Select
                    value={String(field.value)}
                    onValueChange={(value) => field.onChange(Number(value))}
                  >
                    <SelectTrigger className="h-9 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SLOT_OPTIONS.map((minutes) => (
                        <SelectItem key={minutes} value={String(minutes)}>
                          {minutes} minutes
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
            <div className="grid grid-cols-2 border-b border-[#dededb]">
              <FormField
                control={form.control}
                name="leadTimeHours"
                render={({ field }) => (
                  <CompactInputCell label="Minimum notice" suffix="hours">
                    <Input type="number" min={0} {...field} />
                  </CompactInputCell>
                )}
              />
              <FormField
                control={form.control}
                name="maxAdvanceBookingDays"
                render={({ field }) => (
                  <CompactInputCell label="Booking horizon" suffix="days" bordered>
                    <Input type="number" min={1} {...field} />
                  </CompactInputCell>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="emergencyEnabled"
              render={({ field }) => (
                <div className="border-b border-[#dededb] px-[18px] py-2.5">
                  <p className="mb-1 text-[12.5px] font-medium">Emergencies</p>
                  <label className="flex min-h-9 cursor-pointer items-center gap-2.5 rounded-md border border-[#dededb] px-2.5 text-[12.5px]">
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                    Ignore minimum notice
                  </label>
                </div>
              )}
            />
            <FormField control={form.control} name="allowOverlappingSchedules" render={({ field }) => (
              <div className="border-b border-[#dededb] px-[18px] py-2.5">
                <Label htmlFor="allow-overlapping-schedules">Allow overlapping schedules</Label>
                <select id="allow-overlapping-schedules" className="mt-2 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={field.value ? 'yes' : 'no'} onChange={(event) => field.onChange(event.target.value === 'yes')}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
                <p className="mt-1 text-xs text-muted-foreground">Allow staff to assign a technician to overlapping visits. Conflict warnings remain visible.</p>
              </div>
            )} />
            <div className="border-b border-[#dededb] px-[18px] py-2.5">
              <p className="mb-1 text-[12.5px] font-medium">Live tracking</p>
              <FormField
                control={form.control}
                name="geofenceAutoArrival"
                render={({ field }) => (
                  <>
                    <label className="flex min-h-9 cursor-pointer items-center gap-2.5 rounded-md border border-[#dededb] px-2.5 text-[12.5px]">
                      <Switch
                        checked={field.value}
                        onCheckedChange={(next) => {
                          field.onChange(next);
                          // Auto-start rides on arrival — never leave it armed
                          // once arrival is turned off.
                          if (!next)
                            form.setValue('geofenceAutoStart', false, {
                              shouldDirty: true,
                            });
                        }}
                      />
                      Auto-arrive when the tech reaches the job
                    </label>
                    <p className="mt-1 text-[11.5px] leading-[1.25] text-[#666]">
                      Marks the visit arrived and texts the customer automatically when the
                      technician enters the job location.
                    </p>
                  </>
                )}
              />
              <FormField
                control={form.control}
                name="geofenceAutoStart"
                render={({ field }) => {
                  const arrivalOn = form.watch('geofenceAutoArrival');
                  return (
                    <div className="mt-2 pl-3">
                      <label
                        className={`flex min-h-9 items-center gap-2.5 rounded-md border border-[#dededb] px-2.5 text-[12.5px] ${
                          arrivalOn ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                        }`}
                      >
                        <Switch
                          checked={field.value && arrivalOn}
                          disabled={!arrivalOn}
                          onCheckedChange={field.onChange}
                        />
                        Also start the job and notify the technician
                      </label>
                      <p className="mt-1 text-[11.5px] leading-[1.25] text-[#666]">
                        On arrival, the visit is set to In progress and the technician gets a push
                        notification. Requires auto-arrive.
                      </p>
                    </div>
                  );
                }}
              />
            </div>
            <p className="px-[18px] py-2 text-[11.5px] leading-[1.25] text-[#666]">
              {currentWeeklySlots === defaultWeeklySlots ? (
                <>
                  These settings create{' '}
                  <b className="font-semibold text-[#171719]">{currentWeeklySlots} slots</b> per
                  week before technicians are assigned.
                </>
              ) : (
                <>
                  These values change availability from{' '}
                  <b className="font-semibold text-[#171719]">{defaultWeeklySlots}</b> to{' '}
                  <b className="font-semibold text-[#171719]">{currentWeeklySlots} slots</b> per
                  week.
                </>
              )}
            </p>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-[#dededb] bg-white text-[#171719]">
        <div className="grid lg:grid-cols-2">
          <div className="min-w-0">
            <CompactSectionHeader icon={<Car className="size-3.5" />} title="Around each visit" />
            <div className="space-y-3 px-[18px] py-3">
              <VisitBufferDiagram
                prep={form.watch('bufferPrepMinutes')}
                cleanup={form.watch('bufferCleanupMinutes')}
              />
              <div className="grid grid-cols-2 gap-2.5">
                <InlineNumberField
                  form={form}
                  name="bufferPrepMinutes"
                  label="Preparation"
                  suffix="min"
                  min={0}
                />
                <InlineNumberField
                  form={form}
                  name="bufferCleanupMinutes"
                  label="Cleanup"
                  suffix="min"
                  min={0}
                />
              </div>
              <InlineNumberField
                form={form}
                name="travelAvgSpeedKmh"
                label="Travel speed"
                suffix="km/h"
                min={1}
                max={200}
              />
              <InlineNumberField
                form={form}
                name="maxAutoTimeShiftMinutes"
                label="Route-first window"
                suffix="min"
                min={0}
                max={240}
              />
              <p className="text-[11px] leading-[1.35] text-[#666]">
                Within ± this many minutes, auto-scheduling prefers the route with the least added
                drive. Set to 0 for exact-time priority.
              </p>
            </div>
          </div>

          <div className="min-w-0 border-t border-[#dededb] lg:border-l lg:border-t-0">
            <CompactSectionHeader title="How the calendar opens" />
            <div className="space-y-3 px-[18px] py-3">
              <FormField
                control={form.control}
                name="defaultCalendarView"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[11.5px]">Default view</FormLabel>
                    <div className="grid grid-cols-5 gap-1 rounded-md border border-[#dededb] p-1">
                      {CALENDAR_VIEW_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => field.onChange(option.value)}
                          className={
                            field.value === option.value
                              ? 'rounded-[4px] bg-[#171719] px-1 py-1.5 text-[10.5px] font-semibold text-white'
                              : 'rounded-[4px] px-1 py-1.5 text-[10.5px] font-medium text-[#666]'
                          }
                        >
                          {option.label.replace(' Week', '')}
                        </button>
                      ))}
                    </div>
                  </FormItem>
                )}
              />
              <WeekdaySelectField form={form} name="weekStartDay" label="Week starts" />
              <WeekdaySelectField form={form} name="workWeekStartDay" label="Work week starts" />
            </div>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-[#dededb] bg-white text-[#171719]">
        <div className="grid lg:grid-cols-[minmax(0,936fr)_minmax(0,366fr)]">
          <div className="min-w-0">
            <FormField
              control={form.control}
              name="blackoutDates"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <BlackoutDatesEditor
                      value={field.value as BusinessBlackoutDateInput[]}
                      onChange={field.onChange}
                      mode="companyCalendar"
                      layout="scheduling"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="min-w-0 border-t border-[#dededb] lg:border-l lg:border-t-0">
            <header className="flex h-[31px] items-center gap-2 border-b border-[#dededb] px-[18px]">
              <CloudRain className="size-3.5 text-[#666]" />
              <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#666]">
                Weather flex days
              </h2>
            </header>
            <div className="space-y-[6px] px-[18px] py-[7px]">
              <div className="grid h-[27px] grid-cols-7 gap-1">
                {WEEKDAY_OPTIONS.map((label, weekday) => {
                  const enabled = flexDays.some((day) => day.weekday === weekday);
                  const isActive = enabled && activeFlexWeekday === weekday;
                  return (
                    <div key={label} className="relative h-[27px] min-w-0">
                      <button
                        type="button"
                        onClick={() => {
                          if (!enabled) {
                            setWeatherFlexDay(weekday, true);
                          }
                          setSelectedFlexWeekday(weekday);
                        }}
                        className={
                          isActive
                            ? 'h-[27px] w-full rounded-md border border-[#5b94ee] bg-[#dce9ff] text-[10.5px] font-bold text-[#0f52ba] ring-1 ring-[#5b94ee]'
                            : enabled
                              ? 'h-[27px] w-full rounded-md border border-[#b8d1f8] bg-[#edf4ff] text-[10.5px] font-semibold text-[#3169ad]'
                              : 'h-[27px] w-full rounded-md border border-[#dededb] text-[10.5px] font-medium text-[#666]'
                        }
                        aria-pressed={enabled}
                        aria-current={isActive ? 'true' : undefined}
                        title={
                          isActive
                            ? `Editing ${label} capacity`
                            : enabled
                              ? `Edit ${label} capacity`
                              : `Add ${label} as a weather flex day`
                        }
                      >
                        {label.slice(0, 1)}
                      </button>
                      {isActive ? (
                        <button
                          type="button"
                          onClick={() => {
                            setWeatherFlexDay(weekday, false);
                            const nextSelected = flexDays.find((day) => day.weekday !== weekday);
                            if (nextSelected) setSelectedFlexWeekday(nextSelected.weekday);
                          }}
                          className="absolute right-0.5 top-0.5 inline-flex size-3 items-center justify-center rounded-sm text-[#dc2626] hover:bg-[#fee2e2]"
                          aria-label={`Remove ${label} from weather flex days`}
                          title={`Remove ${label}`}
                        >
                          <X className="size-2.5 stroke-[2.5]" />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {selectedFlexConfig ? (
                <div className="h-[56px]">
                  <label className="mb-[3px] block text-[11px] font-medium leading-[14px]">
                    {WEEKDAY_OPTIONS[activeFlexWeekday]} capacity
                  </label>
                  <div className="flex h-[38px] items-center overflow-hidden rounded-md border border-[#dededb]">
                    <Input
                      type="number"
                      min={1}
                      value={selectedFlexConfig.maxVisitsPerTech ?? ''}
                      onChange={(event) =>
                        setWeatherFlexLimit(activeFlexWeekday, event.target.value)
                      }
                      placeholder="No cap"
                      className="h-9 border-0 shadow-none focus-visible:ring-0"
                    />
                    <span className="px-2.5 text-[11px] text-[#666]">visits / tech</span>
                  </div>
                </div>
              ) : (
                <p className="h-[56px] text-[11px] leading-[14px] text-[#666]">
                  Select a weather flex day.
                </p>
              )}
              <FormField
                control={form.control}
                name="weatherFlexSpillover"
                render={({ field }) => (
                  <FormItem className="h-[56px] space-y-0">
                    <FormLabel className="mb-[3px] block text-[11px] leading-[14px]">
                      When a flex day is full
                    </FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-[38px] w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NEXT_FLEX_DAY">Use next flex day</SelectItem>
                        <SelectItem value="EXTEND_WINDOW">Extend the work window</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function CompactSectionHeader({ icon, title }: { icon?: ReactNode; title: string }) {
  return (
    <header className="flex min-h-[43px] items-center gap-2 border-b border-[#dededb] px-[18px]">
      {icon ? <span className="text-[#666]">{icon}</span> : null}
      <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#666]">{title}</h2>
    </header>
  );
}

function CompactInputCell({
  label,
  suffix,
  bordered,
  children,
}: {
  label: string;
  suffix: string;
  bordered?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={bordered ? 'border-l border-[#dededb] px-[18px] py-2.5' : 'px-[18px] py-2.5'}>
      <label className="mb-1 block text-[11.5px] font-medium">{label}</label>
      <div className="flex items-center overflow-hidden rounded-md border border-[#dededb]">
        <div className="min-w-0 flex-1 [&_input]:h-9 [&_input]:border-0 [&_input]:shadow-none [&_input]:focus-visible:ring-0">
          {children}
        </div>
        <span className="shrink-0 px-2 text-[11.5px] text-[#666]">{suffix}</span>
      </div>
    </div>
  );
}

function calculateWeeklySlots(hours: BusinessHours[], slotMinutes: number) {
  const totalMinutes = hours.reduce((total, day) => {
    if (!day.open) return total;
    const [openHour, openMinute] = day.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = day.closeTime.split(':').map(Number);
    return total + closeHour * 60 + closeMinute - (openHour * 60 + openMinute);
  }, 0);

  return slotMinutes > 0 ? Math.floor(totalMinutes / slotMinutes) : 0;
}

type InlineNumberName =
  | 'bufferPrepMinutes'
  | 'bufferCleanupMinutes'
  | 'travelAvgSpeedKmh'
  | 'maxAutoTimeShiftMinutes'
  | 'committedWindowToleranceMin'
  | 'efficiencyAlertThresholdPct';

function InlineNumberField({
  form,
  name,
  label,
  suffix,
  min,
  max,
}: {
  form: UseFormReturn<FormValues>;
  name: InlineNumberName;
  label: string;
  suffix: string;
  min: number;
  max?: number;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-[11.5px]">{label}</FormLabel>
          <div className="flex items-center overflow-hidden rounded-md border border-[#dededb]">
            <Input
              type="number"
              min={min}
              max={max}
              {...field}
              className="h-9 border-0 shadow-none focus-visible:ring-0"
            />
            <span className="whitespace-nowrap px-2.5 text-[11.5px] text-[#666]">{suffix}</span>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function WeekdaySelectField({
  form,
  name,
  label,
}: {
  form: UseFormReturn<FormValues>;
  name: 'weekStartDay' | 'workWeekStartDay';
  label: string;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-[11.5px]">{label}</FormLabel>
          <Select
            value={String(field.value)}
            onValueChange={(value) => field.onChange(Number(value))}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WEEKDAY_OPTIONS.map((day, index) => (
                <SelectItem key={day} value={String(index)}>
                  {day}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
